// src/components/institution/sections/EventsSection.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, ChevronRight, Eye, Star, Tag } from 'lucide-react';
import { institutionService } from '../../../services/institutionService';

export const EventsSection = ({ bookId, data, settings, styling, onEventClick }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const toDate = (yyyy_mm_dd, hh_mm) => {
    if (!yyyy_mm_dd) return null;
    return new Date(`${yyyy_mm_dd}T${hh_mm || '00:00'}:00`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventStatus = (startDate, endDate, startTime, endTime) => {
    const now = new Date();
    const start = toDate(startDate, startTime) || (startDate ? new Date(startDate) : null);
    const end =
      toDate(endDate, endTime) ||
      (endDate ? new Date(`${endDate}T23:59:59`) : (start ? new Date(`${startDate}T23:59:59`) : null));

    if (start && end) {
      if (now < start) return 'upcoming';
      if (now >= start && now <= end) return 'ongoing';
      return 'past';
    }
    if (start) {
      if (now.toDateString() === start.toDateString()) return 'ongoing';
      return now < start ? 'upcoming' : 'past';
    }
    return 'unknown';
  };

  const loadEvents = async () => {
    try {
      const eventsData = await institutionService.getEvents(bookId);
      const list = Array.isArray(eventsData) ? eventsData : [];

      // Only show public events on the public section unless explicitly configured
      const onlyPublic = settings?.showPrivate !== true;
      const filtered = list.filter((e) => (onlyPublic ? e.isPublic !== false : true));

      // Upcoming or ongoing; sort by start date/time
      const upcomingOrOngoing = filtered
        .filter((e) => {
          const status = getEventStatus(e.startDate, e.endDate, e.startTime, e.endTime);
          return status === 'upcoming' || status === 'ongoing';
        })
        .sort((a, b) => {
          const aStart = toDate(a.startDate, a.startTime) || new Date(a.startDate || 0);
          const bStart = toDate(b.startDate, b.startTime) || new Date(b.startDate || 0);
          return aStart - bStart;
        })
        .slice(0, 6);

      setEvents(upcomingOrOngoing);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Upcoming Events
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor || '#1e40af' }} />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest events and activities.
          </p>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Upcoming Events</h3>
            <p className="text-gray-500">Check back soon for exciting events and activities!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => {
              const {
                id,
                title,
                description,
                category,
                location,
                startDate,
                endDate,
                startTime,
                endTime,
                capacity,
                isPublic,
                isFeatured,
                tags,
                eventImage
              } = event;

              const primary = styling?.primaryColor || '#1e40af';

              return (
                <div
                  key={id}
                  className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                  onClick={() => onEventClick && onEventClick(event)}
                >
                  {eventImage && eventImage.trim() !== '' && (
                    <img
                      src={eventImage}
                      alt={title}
                      className="w-full h-48 object-cover"
                    />
                  )}

                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <div
                        className="px-3 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: primary }}
                      >
                        {category || 'Event'}
                      </div>

                      {isPublic && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                          <Eye className="h-3.5 w-3.5" /> Public
                        </span>
                      )}

                      {isFeatured && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
                          <Star className="h-3.5 w-3.5" /> Featured
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {title}
                    </h3>

                    {description && (
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {description}
                      </p>
                    )}

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {formatDate(startDate)}
                          {endDate && startDate !== endDate ? ` – ${formatDate(endDate)}` : ''}
                        </span>
                      </div>

                      {(startTime || endTime) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>
                            {startTime ? formatTime(startTime) : ''}
                            {endTime ? ` – ${formatTime(endTime)}` : ''}
                          </span>
                        </div>
                      )}

                      {location && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{location}</span>
                        </div>
                      )}

                      {capacity && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users className="h-4 w-4" />
                          <span>Capacity: {parseInt(capacity, 10) || capacity}</span>
                        </div>
                      )}
                    </div>

                    {Array.isArray(tags) && tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <Tag className="h-4 w-4 text-gray-400" />
                        {tags.map((t) => (
                          <span
                            key={t}
                            className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    <button
                      className="w-full border-2 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                      style={{
                        borderColor: primary,
                        color: primary
                      }}
                    >
                      View Details
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
